import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subject, takeUntil } from 'rxjs';
import { MaterialModule } from '@app/material.module';
import { DevicesApiService } from '@services/api/devices/devices-api.service';
import { AuthService } from '@services/api/auth/auth.service';
import { ToastrServices } from '@services/toastr/toastr.service';
import { ModalsConfirmComponent } from '@components/modals/modals-confirm/modals-confirm.component';
import { DeviceFormComponent, DeviceFormMode } from '@pages/devices/device-form/device-form.component';
import { Device } from '@interfaces/device';
import { User } from '@interfaces/user';
import { Roles } from '@enums/Roles';
import { CompanyFilterComponent } from '@components/company-filter/company-filter.component';
import { CompanyFilterService } from '@services/company-filter/company-filter.service';

/** Headsets and phones allowed to log in with a technician's QR: new ones wait here for approval. */
@Component({
  selector: 'app-devices',
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, TranslateModule, TablerIconsModule, CompanyFilterComponent],
  standalone: true,
  templateUrl: './devices.component.html',
  styleUrl: './devices.component.scss'
})
export class DevicesComponent implements OnInit, OnDestroy {
  private devices_api_service = inject(DevicesApiService);
  private user_api_service = inject(AuthService);
  private toastr_service = inject(ToastrServices);
  private translate = inject(TranslateService);
  private companyFilter = inject(CompanyFilterService);
  protected dialog = inject(MatDialog);
  protected readonly Roles = Roles;
  protected user: User | null = null;
  protected devices: Device[] = [];
  protected filteredDevices: Device[] = [];
  protected loaded = false;
  // The three summary tiles double as status filters
  protected statusFilter: 'all' | 'pending' | 'enabled' = 'all';
  searchControl = new FormControl('', { nonNullable: true });
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.user_api_service.get_user_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (response) => this.user = response.data, error: (error) => console.error(error) });
    this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyFilter());
    this.companyFilter.selected$.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyFilter());
    this.loadDevices();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Only the superadmin approves, adds and deletes devices (the API refuses the others since 02/11);
  // the backoffice and remote technicians only see their company's list
  get canEdit(): boolean {
    return this.user?.roles?.id === Roles.SUPER_ADMIN;
  }

  // The backoffice asks us for new devices of its company; we approve them
  get canRequest(): boolean {
    return this.user?.roles?.id === Roles.MANAGEMENT_SYSTEM;
  }

  // Devices of the company chosen in the superadmin's "Azienda" menu
  get companyDevices(): Device[] {
    return this.devices.filter(device => this.companyFilter.matches(device.company));
  }

  get columns(): string[] {
    const columns = ['name', 'serial_number'];
    if (this.user?.roles?.id === Roles.SUPER_ADMIN) {
      columns.push('company');
    }
    columns.push('status');
    if (this.canEdit) {
      columns.push('actions');
    }
    return columns;
  }

  get pendingCount(): number {
    return this.companyDevices.filter(device => !device.enabled).length;
  }

  get enabledCount(): number {
    return this.companyDevices.filter(device => device.enabled).length;
  }

  loadDevices(): void {
    this.devices_api_service.get_devices_list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.devices = response.data ?? [];
          this.loaded = true;
          this.applyFilter();
        },
        error: (error) => {
          // anche in caso di errore si mostra lo stato vuoto invece di un'area bianca
          console.error(error);
          this.loaded = true;
          this.applyFilter();
        }
      });
  }

  setStatusFilter(filter: 'all' | 'pending' | 'enabled'): void {
    this.statusFilter = filter;
    this.applyFilter();
  }

  applyFilter(): void {
    const search = this.searchControl.value.trim().toLowerCase();
    const byStatus = this.companyDevices.filter(device =>
      this.statusFilter === 'all' || (this.statusFilter === 'enabled' ? device.enabled : !device.enabled));
    this.filteredDevices = !search ? byStatus : byStatus.filter(device =>
      [device.name, device.serial_number, device.company_name, device.brand, device.model]
        .some(value => value?.toLowerCase().includes(search)));
  }

  setEnabled(device: Device, enabled: boolean): void {
    // A headset that asked by itself does not say whether it is a headset or a phone: complete it before approving
    if (enabled && (!device.device_type || !device.usage)) {
      this.toastr_service.showInfo(this.translate.instant('devices.complete_labels'));
      this.openForm(device);
      return;
    }
    this.devices_api_service.update_device(device.id, { enabled })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr_service.showSuccess(this.translate.instant(enabled ? 'devices.approved' : 'devices.disabled'));
          this.loadDevices();
        },
        error: () => this.toastr_service.showError(this.translate.instant('devices.error_save'))
      });
  }

  openForm(device: Device | null, mode: DeviceFormMode = device ? 'edit' : 'add'): void {
    this.dialog.open(DeviceFormComponent, {
      data: { device, mode },
      autoFocus: false,
      width: '100%',
      maxWidth: '520px',
      panelClass: ['xr-dialog', 'device-form-dialog'],
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((saved?: DeviceFormMode) => {
        // The form saves by itself and closes with its mode only when the server accepted the device
        if (!saved) {
          return;
        }
        this.toastr_service.showSuccess(this.translate.instant(saved === 'request' ? 'devices.request_sent' : 'devices.saved'));
        this.loadDevices();
      });
  }

  deleteDevice(device: Device): void {
    this.dialog.open(ModalsConfirmComponent, {
      data: { title: 'devices.delete', message: 'devices.delete_confirm', confirmText: 'devices.confirm', cancelText: 'devices.cancel' },
      width: '100%',
      maxWidth: '400px',
      panelClass: 'xr-dialog',
      autoFocus: false
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.devices_api_service.delete_device(device.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastr_service.showSuccess(this.translate.instant('devices.deleted'));
              this.loadDevices();
            },
            error: () => this.toastr_service.showError(this.translate.instant('devices.error_delete'))
          });
      });
  }
}
